import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Check, Search } from 'lucide-react';

import {
  COUNTRIES,
  DEFAULT_ISO,
  composeE164,
  countryByIso,
  isoToFlag,
  parseE164,
} from './countries';

export interface PhoneInputProps {
  /** Full E.164 value, e.g. '+525551234567' (controlled). '' when empty. */
  value: string;
  /** Fires with the composed E.164 string (or '' when the number is empty). */
  onChange: (e164: string) => void;
  label?: string;
  labelIcon?: ReactNode;
  hint?: string;
  error?: string;
  /** Placeholder for the national-number field (no country code). */
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Phone entry with a country picker: the user selects their country (flag +
 * dial code) and types only the national number, and this composes the full
 * E.164 string the SMS/verification flow needs — so nobody has to type "+52".
 *
 * Controlled by a single E.164 `value`/`onChange` so it drops in wherever a
 * plain E.164 <Input> was used without touching the caller's validation,
 * verification, or persistence: the emitted string is byte-identical to what a
 * user would have typed by hand. Typing or pasting a value that starts with
 * '+' into the number field is still honoured — it's re-parsed back into the
 * matching country + national number — which also keeps existing tests (which
 * type a full E.164) working unchanged.
 */
export function PhoneInput({
  value,
  onChange,
  label,
  labelIcon,
  hint,
  error,
  placeholder = '555 123 4567',
  disabled = false,
  id,
}: PhoneInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  // Country lives in local state (a bare dial code can't be recovered from an
  // empty `value`); the national part is kept here too and re-synced from
  // `value` whenever an external change (prefill, reset) doesn't already match
  // what our state composes to.
  const [iso, setIso] = useState(() =>
    value ? parseE164(value).iso : DEFAULT_ISO,
  );
  const [national, setNational] = useState(() =>
    value ? parseE164(value).national : '',
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);

  // Re-sync from an externally-driven value (server prefill, remove→'') that
  // our current country + national don't already produce. Guarded by the
  // equality check so our own emits don't bounce back and cause a loop.
  useEffect(() => {
    if (value === composeE164(iso, national)) return;
    if (value) {
      const parsed = parseE164(value);
      setIso(parsed.iso);
      setNational(parsed.national);
    } else {
      setNational('');
    }
    // Only react to external `value` changes; internal edits emit + update
    // state together and are covered by the equality guard above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close the dropdown on outside click / Escape (same behaviour as Select).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // `null` when the pasted number's dial code is not in the catalogue. The
  // picker then shows a globe and a bare `+` rather than a country we guessed:
  // showing a flag here is what made the rewrite invisible, because the field
  // looked like a correctly-parsed US number (YAR-410).
  const country = iso === null ? null : countryByIso(iso);

  function selectCountry(nextIso: string) {
    setIso(nextIso);
    setOpen(false);
    setQuery('');
    onChange(composeE164(nextIso, national));
    numberRef.current?.focus();
  }

  function handleNumberChange(raw: string) {
    // Honour a pasted/typed full E.164: re-derive the country + national from
    // it and emit it verbatim (round-trips exactly for known dial codes).
    if (raw.trim().startsWith('+')) {
      const parsed = parseE164(raw);
      setIso(parsed.iso);
      setNational(parsed.national);
      onChange(composeE164(parsed.iso, parsed.national));
      return;
    }
    const nat = raw.replace(/\D/g, '');
    setNational(nat);
    onChange(composeE164(iso, nat));
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.dial.slice(1).includes(q.replace('+', '')),
      )
    : COUNTRIES;

  const describedBy = error
    ? `${inputId}-error`
    : hint
      ? `${inputId}-hint`
      : undefined;

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-2 w-full"
    >
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-foreground leading-5 flex items-center gap-1"
        >
          {labelIcon}
          {label}
        </label>
      )}

      <div className="relative flex gap-2">
        {/* Country selector */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-label={
            country
              ? `Country: ${country.name} (${country.dial})`
              : 'Country: not in the list — the number is kept exactly as entered'
          }
          aria-haspopup="listbox"
          aria-expanded={open}
          className={[
            'shrink-0 inline-flex items-center gap-1.5 h-10 rounded-md border bg-white',
            // Dial code is neutral (#5e5e5e), not body copy — it labels the
            // picker rather than carrying a value the user typed (YAR-284).
            'px-2.5 text-sm text-muted-foreground',
            'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
            'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
            'transition-colors duration-150 cursor-pointer',
            // PhoneInput does not wrap Input — it re-declares the field
            // styling — so the resting border has to be corrected here too, or
            // the phone row is the one field left at the old #d1d1d1.
            open ? 'border-border-focus' : 'border-border',
          ].join(' ')}
        >
          <span
            className="text-base leading-none"
            aria-hidden="true"
          >
            {country ? isoToFlag(country.iso) : '🌐'}
          </span>
          <span className="tabular-nums">{country ? country.dial : '+'}</span>
          {open ? (
            <ChevronUp
              size={16}
              className="text-icon-default"
              aria-hidden="true"
            />
          ) : (
            <ChevronDown
              size={16}
              className="text-icon-default"
              aria-hidden="true"
            />
          )}
        </button>

        {/* National number */}
        <input
          ref={numberRef}
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={placeholder}
          value={national}
          disabled={disabled}
          onChange={(e) => handleNumberChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'flex-1 min-w-0 h-10 rounded-md border bg-white text-foreground text-sm leading-5',
            'px-3 py-2',
            'placeholder:text-text-placeholder',
            'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
            'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
            'transition-colors duration-150',
            // Mirrors Input: #e5e5e5 at rest, #a82222 on error (not #c94040).
            error ? 'border-border-error' : 'border-border',
          ].join(' ')}
        />

        {/* Country dropdown */}
        {open && (
          <div
            role="listbox"
            aria-label="Country"
            className="absolute left-0 top-[calc(100%+0.25rem)] z-[50] w-72 max-w-full rounded-lg border border-border bg-white shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-border">
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-icon-default pointer-events-none">
                  <Search
                    size={16}
                    aria-hidden="true"
                  />
                </span>
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search countries"
                  aria-label="Search countries"
                  className="w-full h-9 rounded-md border border-border bg-white pl-8 pr-3 text-sm text-foreground placeholder:text-text-placeholder focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">
                  No matches
                </p>
              ) : (
                filtered.map((c) => {
                  const selected = c.iso === iso;
                  return (
                    <button
                      key={c.iso}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => selectCountry(c.iso)}
                      className={[
                        'w-full flex items-center gap-2.5 px-3 py-2.5 text-left cursor-pointer text-sm',
                        selected ? 'bg-muted' : 'hover:bg-muted/50',
                      ].join(' ')}
                    >
                      <span
                        className="text-base leading-none"
                        aria-hidden="true"
                      >
                        {isoToFlag(c.iso)}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-foreground">
                        {c.name}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {c.dial}
                      </span>
                      {selected && (
                        <Check
                          size={16}
                          className="shrink-0 text-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p
          id={`${inputId}-error`}
          className="text-sm text-text-error leading-5"
          role="alert"
        >
          {error}
        </p>
      )}
      {/* Hint copy sits at the placeholder tone (#7a7a7a) — "Pick your
          country, then enter your number" (YAR-284). */}
      {!error && hint && (
        <p
          id={`${inputId}-hint`}
          className="text-sm text-text-placeholder leading-5"
        >
          {hint}
        </p>
      )}
    </div>
  );
}

PhoneInput.displayName = 'PhoneInput';
